import { PlayerExtended } from 'components/Player'
import produce from 'immer'
import {
  createContext,
  Dispatch,
  FunctionComponent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useReducer
} from 'react'

export type PlaybackType = 'vod' | 'live' | 'unknown'
export type VideoType = 'highlight' | 'event'

export interface YouboraOptions {
  username: string
  'content.isLive': boolean
  'content.title': string
  'extraparam.1': VideoType
  'extraparam.2': string
  'extraparam.3': string
  'extraparam.4': string
  'extraparam.5': string
  'extraparam.6': string
  'extraparam.7': string
  'extraparam.8': string
  'extraparam.9': string
  'extraparam.10': string
  'extraparam.11': string
  'extraparam.12': string
  'extraparam.13': boolean
}

export interface PlayerState {
  isAtLiveEdge: boolean | null
  isInitialized: boolean
  isCasting: boolean
  isFullscreen: boolean | null
  isInfoOpen: boolean | null
  isLive: boolean | null
  isMediaControlOpen: boolean | null
  isPlaying: boolean | null
  isSeeking: boolean | null
  isStreamError: boolean
  playerHeight: number
  playerInstance: PlayerExtended | null
  playerWidth: number
  totalViewers: number
  totalTime: number
  currentTime: number
  playbackType: PlaybackType
  startTimeOffset: number
  youboraOptions?: YouboraOptions
}

export type Action =
  | { type: 'initialize'; instance: PlayerExtended }
  | { type: 'dispose' }
  | { type: 'disableControls' }
  | { type: 'enableControls' }
  | { type: 'openInfo' }
  | { type: 'closeInfo' }
  | { type: 'closeMediaControl' }
  | { type: 'replay'; elapsedTime: number; duration: number }
  | { type: 'updateTotalViewers'; value: number }
  | { type: 'updateYouboraOptions'; value: YouboraOptions }
  | { type: 'updateTotalTime'; value: number }
  | { type: 'updateCurrentTime'; value: number }
  | { type: 'resize' }
  | { type: 'playerError'; value: string }

export type PlayerActions =
  | { type: 'playerUserActive' }
  | { type: 'playerUserInactive' }
  | { type: 'playerFirstPlay' }
  | { type: 'playerPlaying' }
  | { type: 'playerPause' }
  | { type: 'playerEnded' }
  | { type: 'playerSeekStart' }
  | { type: 'playerSeekEnd' }
  | { type: 'playerFullscrenChange' }
  | { type: 'playerChromecastConnected' }
  | { type: 'playerChromecastDisconnected' }
  | { type: 'playerTimeUpdate' }
  | { type: 'playerQualitySelectorInitialize' }
  | { type: 'playerLiveEdgeChange' }

const initialPlayerState: PlayerState = {
  isAtLiveEdge: null,
  isInitialized: false,
  isCasting: false,
  isFullscreen: null,
  isInfoOpen: null,
  isLive: null,
  isMediaControlOpen: null,
  isPlaying: null,
  isSeeking: null,
  isStreamError: false,
  playerHeight: 0,
  playerInstance: null,
  playerWidth: 0,
  totalViewers: 0,
  totalTime: 0,
  currentTime: 0,
  playbackType: 'unknown',
  startTimeOffset: 0,
}

type ReduceType = (
  base: PlayerState,
  action: Action | PlayerActions,
) => PlayerState

const reducer = produce(
  (draft: PlayerState, action: Action | PlayerActions) => {
    if (draft.playerInstance?.isDisposed()) return

    switch (action.type) {
      case 'initialize': {
        if (action.instance?.isDisposed()) return

        draft.playerInstance = action.instance
        draft.isInitialized = true
        draft.playerWidth = draft.playerInstance.currentWidth() || 0
        draft.playerHeight = draft.playerInstance.currentHeight() || 0

        return
      }
      case 'dispose': {
        draft.isInitialized = false
        draft.playerInstance?.dispose()
        draft.playerInstance = null
        draft.playerInstance
        return
      }
      case 'disableControls': {
        draft.playerInstance?.controlBar.addClass('.vjs-control-disabled')
        return
      }
      case 'enableControls': {
        draft.playerInstance?.controlBar.removeClass('.vjs-control-disabled')
        return
      }
      case 'replay': {
        const offset = draft.playbackType === 'live' ? 9 : 0
        const seekTo = Math.max(
          action.elapsedTime - (Math.ceil(action.duration / 2) + offset),
          1,
        )
        draft.playerInstance?.currentTime(seekTo)
        return
      }
      case 'updateTotalViewers': {
        draft.totalViewers = action.value
        return
      }

      case 'updateYouboraOptions': {
        draft.youboraOptions = action.value
        return
      }

      case 'updateTotalTime': {
        draft.totalTime = action.value
        return
      }

      case 'updateCurrentTime': {
        draft.currentTime = action.value
        draft.playerInstance?.currentTime(action.value)
        return
      }

      // Player triggered actions
      case 'playerUserActive': {
        draft.isMediaControlOpen = true
        return
      }

      case 'playerUserInactive': {
        draft.isMediaControlOpen = false
        return
      }

      case 'playerFirstPlay': {
        draft.playbackType = draft.playerInstance?.liveTracker?.isLive?.()
          ? 'live'
          : 'vod'
        draft.isLive = draft.playbackType === 'live'
        draft.isMediaControlOpen = true
        return
      }

      case 'playerPlaying': {
        draft.isPlaying = true
        return
      }

      case 'playerPause': {
        draft.isPlaying = false
        return
      }

      case 'playerEnded': {
        draft.isPlaying = false
        return
      }

      case 'playerSeekStart': {
        draft.isSeeking = true
        return
      }

      case 'playerSeekEnd': {
        draft.isSeeking = false
        return
      }

      case 'playerFullscrenChange': {
        draft.isFullscreen = draft.playerInstance?.isFullscreen() || false
        return
      }

      case 'playerChromecastConnected': {
        draft.isCasting = true
        return
      }

      case 'playerChromecastDisconnected': {
        draft.isCasting = false
        return
      }

      case 'playerTimeUpdate': {
        if (!draft.playerInstance || draft.playerInstance?.isDisposed()) return

        draft.currentTime = Math.floor(draft.playerInstance?.currentTime())
        draft.totalTime =
          draft.playbackType === 'live'
            ? Math.floor(draft.playerInstance.liveTracker?.liveCurrentTime())
            : Math.floor(draft.playerInstance?.duration())
        return
      }

      case 'playerQualitySelectorInitialize': {
        if (!draft.playerInstance || !draft.playerInstance.el()) return

        const levels = draft.playerInstance?.qualityLevels?.().levels_ || []
        // if there are quality levels with undefined height, hide the quality selector
        if (
          levels.filter((level: { height?: number }) => !level.height).length >
          0
        ) {
          draft.playerInstance
            .el()
            .querySelector('.vjs-quality-selector')
            ?.remove()
        }
        return
      }

      case 'playerLiveEdgeChange': {
        draft.playbackType = draft.playerInstance?.liveTracker?.isLive?.()
          ? 'live'
          : 'vod'
        draft.isAtLiveEdge = draft.playerInstance?.liveTracker?.atLiveEdge()
          ? true
          : false
        draft.isLive = draft.playbackType === 'live'
        return
      }

      case 'openInfo': {
        draft.isInfoOpen = true
        return
      }

      case 'closeInfo': {
        draft.isInfoOpen = false
        return
      }

      case 'closeMediaControl': {
        draft.playerInstance?.userActive(false)
        return
      }

      case 'resize': {
        draft.playerWidth = draft.playerInstance?.currentWidth() || 0
        draft.playerHeight = draft.playerInstance?.currentHeight() || 0

        return
      }

      case 'playerError': {
        draft.isStreamError = true
        return
      }

      default:
        throw new Error()
    }
  },
)

export const PlayerStateContext = createContext({
  state: initialPlayerState,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,  @typescript-eslint/no-empty-function
  dispatch: (_a: Action) => {},
})

export function usePlayerState(): PlayerState {
  const ctx = useContext(PlayerStateContext)
  return ctx.state
}

export function usePlayerTimers(): {
  currentTime: number
  totalTime: number
  videoOffset: number
} {
  const ctx = useContext(PlayerStateContext)

  return {
    currentTime: ctx.state.currentTime,
    totalTime: ctx.state.totalTime,
    videoOffset: ctx.state.startTimeOffset,
  }
}

export function usePlayerInstance(): PlayerExtended | null {
  const ctx = useContext(PlayerStateContext)
  return ctx.state.playerInstance
}

export function useDispatch(): Dispatch<Action> {
  const ctx = useContext(PlayerStateContext)
  return ctx.dispatch
}

export function useOnPlayerInit(): (videoJsPlayer: PlayerExtended) => void {
  const dispatch = useDispatch()
  const onPlayerInit = useCallback(
    (videoJsPlayer: PlayerExtended) => {
      dispatch({ type: 'initialize', instance: videoJsPlayer })
    },
    [dispatch],
  )

  return onPlayerInit
}

export const PlayerStateContextProvider: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(
    reducer as ReduceType,
    initialPlayerState,
  )

  useEffect(() => {
    const playerInstance = state.playerInstance
    if (!state.isInitialized || !playerInstance || playerInstance?.isDisposed())
      return

    playerInstance.on('useractive', () => {
      dispatch({ type: 'playerUserActive' })
    })

    playerInstance.on('userinactive', () => {
      dispatch({ type: 'playerUserInactive' })
    })

    playerInstance.on('firstplay', () => {
      dispatch({ type: 'playerFirstPlay' })
    })

    playerInstance.on('playing', () => {
      dispatch({ type: 'playerPlaying' })
    })

    playerInstance.on('pause', () => {
      dispatch({ type: 'playerPause' })
    })

    playerInstance.on('ended', () => {
      dispatch({ type: 'playerEnded' })
    })

    playerInstance.on('seeking', () => {
      dispatch({ type: 'playerSeekStart' })
    })

    playerInstance.on('seeked', () => {
      dispatch({ type: 'playerSeekEnd' })
    })

    playerInstance.on('fullscreenchange', () => {
      dispatch({ type: 'playerFullscrenChange' })
    })

    playerInstance.on('chromecastConnected', () => {
      dispatch({ type: 'playerChromecastConnected' })
    })

    playerInstance.on('chromecastDisconnected', () => {
      dispatch({ type: 'playerChromecastDisconnected' })
    })

    playerInstance.on('timeupdate', () => {
      dispatch({ type: 'playerTimeUpdate' })
    })

    playerInstance?.qualityLevels?.().on('addqualitylevel', () => {
      dispatch({ type: 'playerQualitySelectorInitialize' })
    })

    playerInstance?.liveTracker.on('liveedgechange', () => {
      dispatch({ type: 'playerLiveEdgeChange' })
    })

    playerInstance?.on('error', (error: string) => {
      dispatch({ type: 'playerError', value: error })
    })

    const triggerResize = () => {
      dispatch({ type: 'resize' })
    }

    playerInstance?.on('firstplay', triggerResize)

    window.addEventListener('resize', triggerResize)

    return () => {
      window.removeEventListener('resize', triggerResize)
    }
  }, [state.isInitialized, state.playerInstance, dispatch])

  return (
    <PlayerStateContext.Provider value={{ state, dispatch }}>
      {children}
    </PlayerStateContext.Provider>
  )
}
